import {
  IoBuildOutline,
  IoCheckmarkOutline,
  IoSettingsOutline,
  IoShieldCheckmarkOutline,
  IoTrashBinOutline,
  IoSyncOutline,
  IoDocumentOutline,
  IoMailOutline,
  IoCopyOutline,
  IoChatbubbleOutline,
} from "react-icons/io5";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getAccountStatusProperties } from "./../../../services/accountService";
import Dropdown from "./../../../components/common/Dropdown";
import moment from "moment";
import Modal from "../../../components/common/Modal";
import StatusForm from "./StatusForm";
import ActivationForm from "./ActivationForm";
import DeleteForm from "./DeleteForm";
import SPAForm from "./SPAForm";
import ProposalForm from "./ProposalForm";
import SwornDeclarationForm from "./SwornDeclarationForm";
import Toaster from "../../../components/common/Toaster";
import referralService from "../../../services/referralService";
import authService from "./../../../services/authService";
import SupportForm from "./SupportForm";

const ProfileHeading = ({ data, setData, isLoading }) => {
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [showSPAForm, setShowSPAForm] = useState(false);
  const [showSwornForm, setShowSwornForm] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showActivateForm, setShowActivationForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const user = authService.getCurrentUser();
  const role =
    user["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

  const {
    id,
    short_name,
    referral_code,
    is_profile_completed,
    support_name,
    account_number,
    created_on,
  } = data;

  const date_registered = moment(created_on).fromNow();

  const actions = [
    [
      {
        name: <span className="text-gray-400">Update Status</span>,
        icon: <IoBuildOutline className="mr-5 text-blue-500" />,
        action: () => setShowStatusForm(true),
      },
    ],
    [
      {
        name: <span className="text-gray-400">Send Proposal</span>,
        icon: <IoDocumentOutline className="mr-5 text-gray-400" />,
        action: () => setShowProposalForm(true),
      },
    ],
    [
      {
        name: <span className="text-gray-400">Activate Account</span>,
        icon: <IoCheckmarkOutline className="mr-5 text-green-500" />,
        action: () => setShowActivationForm(true),
      },
    ],
    [
      {
        name: <span className="text-red-400">Delete Account</span>,
        icon: <IoTrashBinOutline className="mr-5 text-red-500" />,
        action: () => setShowDeleteForm(true),
      },
    ],
  ];

  if (role === "Super User")
    actions.push([
      {
        name: <span className="text-gray-400">Edit Support</span>,
        icon: <IoChatbubbleOutline className="mr-5 text-gray-400" />,
        action: () => setShowSupportForm(true),
      },
    ]);

  const { bg_color, text_color } = getAccountStatusProperties(
    data.account_status
  );

  const status_class = `rounded-full h-2 w-2 flex items-center justify-center ${bg_color}`;
  const account_status = (
    <div className="flex items-center mt-3">
      <div className={status_class}></div>
      <span className={`text-xs ml-3 ${text_color}`}>
        {data.account_status_type}
      </span>
    </div>
  );

  const handleGenerateReferralCode = async () => {
    try {
      var code = await referralService.generateReferralCode(id);

      const info = { ...data };
      info["referral_code"] = code;

      setData(info);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  };

  return (
    <>
      {isLoading && (
        <div className="flex-1 animate-pulse">
          <div className="flex items-center">
            <div className="space-y-2 py-1 ml-4">
              <div className="h-4 bg-gray-100 rounded w-full sm:w-40 md:w-80"></div>
              <div className="h-4 bg-gray-100 rounded w-full sm:w-20 md:w-36"></div>
            </div>
          </div>
        </div>
      )}
      {!isLoading && (
        <div className="ml-4">
          <div className="flex justify-between">
            <div>
              <div className="flex items-start">
                <p className="text-gray-500 text-lg flex items-center">
                  <Link to={`/accounts/${data.id}`}>{short_name}</Link>
                  {is_profile_completed && (
                    <IoShieldCheckmarkOutline className="text-blue-500 ml-3" />
                  )}
                </p>
              </div>
              <div className="text-xs text-gray-500">ID: {account_number}</div>
              <div className="mt-5">
                <div className="flex items-center text-xs space-x-2 text-gray-400">
                  <div>Referral Code:</div>
                  {!referral_code && (
                    <button
                      className="flex-1 button button-primary text-xs p-0"
                      onClick={handleGenerateReferralCode}
                    >
                      Generate
                    </button>
                  )}
                  {referral_code && (
                    <div className="flex items-center text-sm text-gray-500">
                      {referral_code}
                      <IoCopyOutline
                        title="Copy Referral Code"
                        className="ml-2 hover:bg-gray-200 rounded-sm cursor-pointer"
                        onClick={() =>
                          navigator.clipboard.writeText(referral_code)
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
              {role === "Super User" && (
                <div>
                  <p className="text-gray-400 text-xs">
                    Support:
                    <span className="ml-2 text-gray-500 text-xs">
                      {support_name}
                    </span>
                  </p>
                </div>
              )}
            </div>
            <Dropdown
              items={actions}
              icon={<IoSettingsOutline className="text-blue-500 text-lg" />}
            />
          </div>
          <div className="flex justify-between items-center mt-4">
            <div>{account_status}</div>
            <div className="text-xs text-gray-400">
              Joined {date_registered}
            </div>
          </div>
        </div>
      )}
      <Modal
        title="Update Status"
        isOpen={showStatusForm}
        isLoading={isFormLoading}
        onClose={() => setShowStatusForm(false)}
        borderTop
      >
        <StatusForm
          data={data}
          setData={setData}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={() => setShowStatusForm(false)}
        />
      </Modal>
      <Modal
        title="Update Support"
        isOpen={showSupportForm}
        isLoading={isFormLoading}
        setLoading={setIsFormLoading}
        onClose={() => setShowSupportForm(false)}
        borderTop
      >
        <SupportForm
          data={data}
          setData={setData}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={() => setShowSupportForm(false)}
        />
      </Modal>
      <Modal
        title="Generate SPA"
        isOpen={showSPAForm}
        isLoading={isFormLoading}
        onClose={() => setShowSPAForm(false)}
        borderTop
      >
        <SPAForm
          data={data}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={() => setShowSPAForm(false)}
        />
      </Modal>
      <Modal
        title="Generate Sworn Declaration"
        isOpen={showSwornForm}
        isLoading={isFormLoading}
        onClose={() => setShowSwornForm(false)}
        borderTop
      >
        <SwornDeclarationForm
          data={data}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={() => setShowSwornForm(false)}
        />
      </Modal>
      <Modal
        isOpen={showProposalForm}
        isLoading={isFormLoading}
        onClose={() => setShowProposalForm(false)}
      >
        <ProposalForm
          data={data}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={() => setShowProposalForm(false)}
        />
      </Modal>
      <Modal
        isOpen={showActivateForm}
        isLoading={isFormLoading}
        onClose={() => setShowActivationForm(false)}
      >
        <ActivationForm
          data={data}
          setData={setData}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onClose={() => setShowActivationForm(false)}
        />
      </Modal>
      <Modal
        isOpen={showDeleteForm}
        isLoading={isFormLoading}
        onClose={() => setShowDeleteForm(false)}
      >
        <DeleteForm
          data={data}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onClose={() => setShowDeleteForm(false)}
        />
      </Modal>
    </>
  );
};

export default ProfileHeading;
